import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { saveTemplateDraft } from '../lib/api/templates.gen';
import { errorMessage, errorReason } from '../lib/errorMessage';
import { toV1 } from '../lib/agentShared/formSchema';
import type { FormSchemaV2 } from '../lib/agentShared/formSchema';

// The template editor's acknowledgement protocol (P04 §7 ¶3, story 4-2d).
//
// ── The one idea ──────────────────────────────────────────────────────────
// "A draft is never described as saved until the server acknowledges its
// revision — the editor's own optimism is not evidence." Before this hook the
// editor posted a hand-rolled `PATCH /report-presets/:id` (`lib/apiClient.
// updateReportPreset`), flipped `dirty` to false in `onSuccess`, and called that
// saved. No revision was involved anywhere in that sentence, so two tabs
// overwrote each other silently and a crash lost whatever had not been posted.
//
// The ONLY thing that moves this hook into the `saved` state is a 200 whose body
// carries `template.revision` and `acknowledgedAt` — both the server's. There is
// no optimistic fifth state, and `dirty` is no longer a thing the page decides:
// "unacknowledged" is computed here, from the gap between the last edit and the
// last acknowledgement, because that gap is the actual fact.
//
// ── The four states P04 §7 ¶3 names ───────────────────────────────────────
//   saving   — a PATCH is in flight. Nothing is claimed.
//   saved    — the server acknowledged. Carries ITS revision and ITS clock.
//   failed   — the write did not land for a reason that is not a conflict. The
//              message is the server's (G-27), never axios's transport summary.
//              Deliberately NOT auto-retried: a timer that reissued a failing
//              write every second would make the state flicker instead of being
//              observable, and would hammer a server that just said no. The next
//              keystroke re-arms it; the Retry control does it now.
//   conflict — the server refused a stale write (`revision_conflict`). Again the
//              server's own sentence, plus the explicit reload-or-overwrite
//              choice P04 §7 ¶3 requires. The editor never silently wins, so
//              autosave SUSPENDS until the user chooses.
// `idle` is a fifth value but not a fifth state: it is the absence of a claim,
// which is what an untouched editor should display.
//
// ── Crash recovery, and why it is localStorage ────────────────────────────
// GI decision for this story. P07's offline outbox (IndexedDB, a real sync
// engine, per-entity conflict policy) is a later wave, and building half of one
// here would be a second sync mechanism to retire. What this story needs is
// narrower and can say so: ONE key per org+template, holding the LAST WHOLE-DRAFT
// SNAPSHOT and the revision it was composed against.
//
// A whole-draft snapshot, never an append log, is what makes "re-running that
// retry twice yields ONE copy of each section/field" true by construction rather
// than by care: replaying a snapshot is idempotent, and the server adds a second
// guard on top (`stageDraft` patches the single draft row instead of appending;
// the write is conditional on the revision, so the second replay is refused).
//
// When P07 lands, its outbox absorbs this — same shape (one pending write per
// entity, carrying the revision it targets), same retry semantics, a better
// store. Everything reads the three functions below rather than localStorage
// directly, so that swap is one file.

/** How long the editor stays quiet after the last keystroke before saving. */
const AUTOSAVE_DEBOUNCE_MS = 1200;

export interface PendingDraft {
  /** The revision this snapshot was composed against — what a retry must quote. */
  ackRevision: number;
  /** The WHOLE draft as the editor held it. Replaying it is idempotent. */
  pendingSchema: FormSchemaV2;
  /**
   * ISO, the CLIENT's clock — the only timestamp the client owns, and
   * deliberately never rendered as a "saved at" time. It answers "how old are
   * these recovered changes", which is a question about this browser.
   */
  savedAt: string;
}

/**
 * Story-scoped, org-scoped, template-scoped. Two orgs in one browser (a
 * consultant in two workspaces, an owner who switched) must never be offered
 * each other's unsaved work.
 */
export const pendingDraftKey = (orgId: string, templateId: string): string => `tpl-draft:${orgId}:${templateId}`;

export function readPendingDraft(orgId: string, templateId: string): PendingDraft | null {
  try {
    const raw = window.localStorage.getItem(pendingDraftKey(orgId, templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingDraft>;
    if (typeof parsed.ackRevision !== 'number' || !parsed.pendingSchema) return null;
    return {
      ackRevision: parsed.ackRevision,
      pendingSchema: parsed.pendingSchema as FormSchemaV2,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : new Date().toISOString(),
    };
  } catch {
    // A corrupt or blocked store must never take the editor down with it — the
    // worst honest outcome is "there was nothing to recover".
    return null;
  }
}

export function writePendingDraft(orgId: string, templateId: string, entry: PendingDraft): void {
  try {
    window.localStorage.setItem(pendingDraftKey(orgId, templateId), JSON.stringify(entry));
  } catch {
    /* storage full or blocked: recovery degrades, editing does not */
  }
}

export function clearPendingDraft(orgId: string, templateId: string): void {
  try {
    window.localStorage.removeItem(pendingDraftKey(orgId, templateId));
  } catch {
    /* see above */
  }
}

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  /** The only state allowed to say "saved", reachable only from a response that
   *  named both of these. */
  | { kind: 'saved'; revision: number; acknowledgedAt: string }
  | { kind: 'failed'; message: string }
  | { kind: 'conflict'; message: string; currentRevision: number; comparisonPath: string | null };

export interface UseTemplateAutosaveArgs {
  /** `null` until a row exists server-side — autosave is inert without a
   *  revision to write against. */
  templateId: string | null;
  orgId: string | null;
  /** The live editor schema. */
  schema: FormSchemaV2;
  /** The revision the server last acknowledged, from the lifecycle read on load. */
  serverRevision: number | null;
  /** False while the row is read-only (P04 §6.2) or still loading. */
  enabled: boolean;
  /** Invalidate the lifecycle read after an acknowledgement: a save stages a
   *  draft, which is what makes submit/publish legal. */
  onAcknowledged?: (revision: number) => void;
  /** The user chose Reload out of a conflict — refetch and re-hydrate. */
  onReload?: () => void;
  /** A recovered snapshot was acknowledged; put it on the canvas, because it is
   *  now the saved truth. */
  onRecoveredApplied?: (schema: FormSchemaV2) => void;
}

export interface UseTemplateAutosave {
  state: SaveState;
  /** True while this tab holds edits the server has not acknowledged. The one
   *  honest source for "Unsaved changes" and for the publish bar's guard. */
  unacknowledged: boolean;
  /** Unacknowledged changes found in the store on open; `null` once resolved. */
  recovered: PendingDraft | null;
  retryRecovered: () => void;
  discardRecovered: () => void;
  /** Conflict exits. Overwrite re-sends the ON-SCREEN draft quoting the revision
   *  the server named — an explicit act, never automatic. */
  overwrite: () => void;
  reload: () => void;
  /** Save now instead of at the end of the debounce (the manual control, and the
   *  Retry on a failure). */
  saveNow: () => void;
  /** Record an edit. Called by the page's change handler rather than inferred
   *  from schema identity, so hydration and agent-driven loads do not read as
   *  user edits. */
  markEdited: () => void;
}

export function useTemplateAutosave({
  templateId,
  orgId,
  schema,
  serverRevision,
  enabled,
  onAcknowledged,
  onReload,
  onRecoveredApplied,
}: UseTemplateAutosaveArgs): UseTemplateAutosave {
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const [recovered, setRecovered] = useState<PendingDraft | null>(null);
  // Monotonic edit counter vs the counter that was acknowledged. Their gap IS
  // "unacknowledged", and it is what stops an ack for edit #3 from clearing the
  // crash-safe copy of edits #4 and #5 typed while it was in flight.
  const [editSeq, setEditSeq] = useState(0);
  const [ackedSeq, setAckedSeq] = useState(0);
  // Re-seeding tick: `reload()` drops the acknowledged revision and asks the page
  // to refetch, and the seed effect has to run again even if the number that
  // comes back happens to be one it has seen.
  const [seedTick, setSeedTick] = useState(0);

  // The revision THIS tab believes the server holds. Seeded from the lifecycle
  // read, advanced only by an acknowledgement — never by an optimistic bump.
  const ackRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const editSeqRef = useRef(0);
  editSeqRef.current = editSeq;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Seed the acknowledged revision, then look for unacknowledged work left by a
  // previous session.
  //
  // It ADOPTS the server's number again whenever there is nothing unacknowledged
  // in this tab, rather than only on first sight. That matters after a conflict
  // reload: `reload()` drops the local revision and asks the page to refetch, and
  // the refetch resolves a tick or two later — a one-shot seed would have latched
  // the stale cached number in between and the very next save would conflict
  // again for no reason. `Math.max` because a revision only ever goes up, so a
  // momentarily stale read can never walk this tab backwards.
  useEffect(() => {
    if (serverRevision === null || !templateId || !orgId) return;
    const first = ackRef.current === null;
    if (first || (editSeq === ackedSeq && !inFlightRef.current)) {
      ackRef.current = Math.max(ackRef.current ?? 0, serverRevision);
    }
    if (!first) return;
    const pending = readPendingDraft(orgId, templateId);
    // Only offer a snapshot that is not already ahead of the server. One
    // targeting a LATER revision cannot be honest about this row (another
    // session, a restored profile); one targeting an earlier revision IS offered,
    // because the retry is exactly how its staleness gets surfaced — as the
    // server's own refusal rather than as a client's guess.
    if (pending && pending.ackRevision <= serverRevision) setRecovered(pending);
    else if (pending) clearPendingDraft(orgId, templateId);
  }, [serverRevision, templateId, orgId, seedTick, editSeq, ackedSeq]);

  const mutation = useMutation({
    mutationFn: async ({ body, expectedRevision }: { body: FormSchemaV2; expectedRevision: number }) => {
      if (!templateId) throw new Error('no template');
      return saveTemplateDraft(templateId, {
        schema: toV1(body) as Record<string, unknown>,
        name: body.title,
        expectedRevision,
      });
    },
  });
  const mutateAsync = mutation.mutateAsync;

  /**
   * The single write path. Autosave, manual save, recovery retry and overwrite
   * all go through here, so the four states are produced in exactly one place
   * and cannot disagree about what happened.
   */
  const send = useCallback(
    async (body: FormSchemaV2, expectedRevision: number, seq: number): Promise<boolean> => {
      if (!templateId || !orgId || inFlightRef.current) return false;
      inFlightRef.current = true;
      setState({ kind: 'saving' });
      try {
        const res = await mutateAsync({ body, expectedRevision });
        const ack = res.data;
        ackRef.current = ack.template.revision;
        // The acknowledgement is the ONLY thing that clears the crash-recovery
        // entry, and only when nothing newer was typed while it was in flight.
        if (seq >= editSeqRef.current) clearPendingDraft(orgId, templateId);
        setAckedSeq(seq);
        setState({ kind: 'saved', revision: ack.template.revision, acknowledgedAt: ack.acknowledgedAt });
        onAcknowledged?.(ack.template.revision);
        return true;
      } catch (err: unknown) {
        const refusal = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
        if (errorReason(err) === 'revision_conflict') {
          // G-27: the server wrote this sentence for a person — it names both
          // revisions and where to compare. Inventing client copy here would
          // throw away the only part of the response that was useful.
          setState({
            kind: 'conflict',
            message: errorMessage(err),
            currentRevision: (refusal?.currentRevision as number) ?? expectedRevision,
            comparisonPath: (refusal?.comparisonPath as string) ?? null,
          });
          return false;
        }
        setState({ kind: 'failed', message: errorMessage(err) });
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [templateId, orgId, mutateAsync, onAcknowledged]
  );

  // Snapshot every edit synchronously, then debounce the network. The ORDER is
  // the whole point: the store is what survives a tab kill, so it is written
  // before the thing that can be interrupted, not after it.
  useEffect(() => {
    if (!enabled || !templateId || !orgId) return undefined;
    if (ackRef.current === null || editSeq === 0 || editSeq === ackedSeq) return undefined;
    // A pending recovery is an unresolved question about which draft is real.
    // Autosaving over it would answer that question on the user's behalf.
    if (recovered) return undefined;
    writePendingDraft(orgId, templateId, {
      ackRevision: ackRef.current,
      pendingSchema: schemaRef.current,
      savedAt: new Date().toISOString(),
    });
    // Conflict: the user owes us a decision. Failed: the next keystroke or the
    // Retry control re-arms it; a timer loop would hide the state rather than
    // expose it.
    if (state.kind === 'conflict' || state.kind === 'failed') return undefined;
    if (inFlightRef.current) return undefined;
    const timer = window.setTimeout(() => {
      const revision = ackRef.current;
      if (revision === null) return;
      void send(schemaRef.current, revision, editSeqRef.current);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [editSeq, ackedSeq, enabled, templateId, orgId, recovered, state, send]);

  const markEdited = useCallback(() => {
    setEditSeq((n) => n + 1);
    // A failure is about the previous attempt; a new edit is a new attempt. A
    // conflict is NOT cleared here — it is a question only the user can answer.
    setState((prev) => (prev.kind === 'failed' ? { kind: 'idle' } : prev));
  }, []);

  const retryRecovered = useCallback(() => {
    if (!recovered) return;
    const snapshot = recovered;
    setRecovered(null);
    // Quoting the snapshot's OWN revision, not today's: that is what makes the
    // replay idempotent AGAINST THE REVISION rather than append-only. Replayed
    // twice, the second attempt is refused as a conflict and surfaces the
    // reload-or-overwrite choice instead of writing a second copy of anything.
    void send(snapshot.pendingSchema, snapshot.ackRevision, editSeqRef.current).then((ok) => {
      if (ok) onRecoveredApplied?.(snapshot.pendingSchema);
    });
  }, [recovered, send, onRecoveredApplied]);

  const discardRecovered = useCallback(() => {
    if (orgId && templateId) clearPendingDraft(orgId, templateId);
    setRecovered(null);
  }, [orgId, templateId]);

  const overwrite = useCallback(() => {
    if (stateRef.current.kind !== 'conflict') return;
    void send(schemaRef.current, stateRef.current.currentRevision, editSeqRef.current);
  }, [send]);

  const reload = useCallback(() => {
    if (orgId && templateId) clearPendingDraft(orgId, templateId);
    ackRef.current = null;
    setRecovered(null);
    setEditSeq(0);
    setAckedSeq(0);
    setState({ kind: 'idle' });
    setSeedTick((n) => n + 1);
    onReload?.();
  }, [orgId, templateId, onReload]);

  const saveNow = useCallback(() => {
    const revision = ackRef.current;
    if (revision === null) return;
    void send(schemaRef.current, revision, editSeqRef.current);
  }, [send]);

  const unacknowledged = editSeq !== ackedSeq || recovered !== null;

  return useMemo(
    () => ({
      state,
      unacknowledged,
      recovered,
      retryRecovered,
      discardRecovered,
      overwrite,
      reload,
      saveNow,
      markEdited,
    }),
    [state, unacknowledged, recovered, retryRecovered, discardRecovered, overwrite, reload, saveNow, markEdited]
  );
}
